import { useEffect, useState } from 'react';

interface UseCountUpOptions {
  /** Target value to count up to */
  end: number;
  /** Starting value (default: 0) */
  start?: number;
  /** Animation duration in milliseconds (default: 2000) */
  duration?: number;
  /** Number of decimal places to show (default: 0) */
  decimals?: number;
  /** Enable animation (default: true) */
  enabled?: boolean;
}

/**
 * Hook to animate counting from start to end value
 */
export const useCountUp = ({
  end,
  start = 0,
  duration = 2000,
  decimals = 0,
  enabled = true,
}: UseCountUpOptions): number => {
  const [count, setCount] = useState(start);

  useEffect(() => {
    if (!enabled) {
      setCount(end);
      return;
    }

    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);

      // Easing function for smooth animation (easeOutExpo)
      const easeOutExpo = percentage === 1 ? 1 : 1 - Math.pow(2, -10 * percentage);

      const currentCount = start + (end - start) * easeOutExpo;
      setCount(Number(currentCount.toFixed(decimals)));

      if (percentage < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [end, start, duration, decimals, enabled]);

  return count;
};
